
import React, { useContext } from 'react';
import { Box, IconButton, Stack } from '@mui/material';
import { MyDataGrid, MyTypography } from '../../common/components';
import DateTimeUtils from '../../common/services/DateTimeUtils';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import AuthContext from '../../auth/context/AuthContext';
import ActiveReferralActionsCell from './components/ActiveReferralActionsCell';
import { common_cols, referralIsOrderColumnItem } from './CommonReferralTable';

export default function ActiveReceivedReferralsTable({
  onFilterChanged,
  onChangeDropdown,
  onSelectedRowsChanged,
  loading,
  data,
  onClick,
  onViewNotes,
}) {

  const currentUser = useContext(AuthContext);

  const renderEditableCell = (params, value) => {
    if (value) return value;

    return (
      <IconButton
        onClick={e => {
          e.stopPropagation();
          params.row.actions.onUpdateReferral(params.row.referral);
        }}
        size="small"
        style={{ marginLeft: 8 }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    );
  };

  const updated_cols = common_cols.map(col => ({
    ...col,
    headerName: col.field === 'mrn' ? 'Sending Clinic MRN' : col.headerName,
    renderCell: params => renderEditableCell(params, col.valueGetter?.(params)),
  }));

  const columns = [
    ...updated_cols,
    {
      field: 'referrer',
      headerName: 'Referral Source',
      width: 320,
      valueGetter: params => {
        const clinic = params.row.referral.getReferrerClinic();
        const provider = params.row.referrer_provider?.display_name || '';
        if (!provider && !clinic) return '';
        return `Clinic: ${clinic || 'N/A'} | Provider: ${provider || 'N/A'}`;
      },
      renderCell: params => {
        const clinic = params.row.referral.getReferrerClinic();
        const provider = params.row.referrer_provider?.display_name || '';
        const hasSource = Boolean(provider || clinic);
        const isFaxReferral = clinic === '★ Referral From Fax';
        if (!hasSource) return renderEditableCell(params, null);
        return (
          <Stack direction="column" spacing={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Stack direction="column" spacing={0.3}>
                <MyTypography variant="subtitle1" component="p" color="text.primary" style={{ margin: 0 }}>
                  Clinic: {clinic || 'N/A'}
                </MyTypography>
                <MyTypography variant="subtitle1" component="p" color="text.primary" style={{ margin: 0 }}>
                  Provider: {provider || 'N/A'}
                </MyTypography>
              </Stack>
              {isFaxReferral && (
                <IconButton
                  onClick={e => {
                    e.stopPropagation();
                    params.row.actions.onUpdateReferrerClinic(params.row.referral);
                  }}
                  size="small"
                  style={{ marginLeft: 8 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            {clinic && clinic.includes('★') && !isFaxReferral && (
              <MyTypography
                variant="h6"
                component="h6"
                color="text.primary"
                style={{ fontStyle: 'italic', margin: 0 }}
              >
                Submitted via Public Form
              </MyTypography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'provider',
      headerName: 'Provider Receiver',
      width: 220,
      valueGetter: params => {
        return params.row.provider?.display_name;
      },
      renderCell: params => {
        const providerName = params.row.provider?.display_name;
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {providerName ? (
              <Stack direction="row" spacing={1}>
                <MyTypography variant="subtitle1">{providerName}</MyTypography>
              </Stack>
            ) : null}
            <IconButton
              onClick={e => {
                e.stopPropagation();
                params.row.actions.onUpdateReferral(params.row.referral);
              }}
              size="small"
              style={{ marginLeft: 8 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </div>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      valueGetter: params => {
        return params.row.referral.getReceiverStatus();
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      headerAlign: 'center',
      sortable: false,
      editable: false,
      filterable: false,
      align: 'center',
      disableColumnMenu: true,
      renderCell: (params) => <ActiveReferralActionsCell row={params.row} type="received" />,
    },
    {
      field: 'appointments',
      headerName: 'Appointment Date or Decline Reason',
      width: 300,
      valueGetter: params => {
        return params.row.referral.getDateOrReason();
      },
      renderCell: (params) => {
        const value = params.row.referral.getDateOrReason();
        const referral = params.row.referral?.referral;

        if (currentUser.actions.can_schedule && !referral.reason) {
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span>{value}</span>
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  params.row.actions.onSetAppointment(params.row.id, referral.appointment, referral.status);
                }}
                size="small"
                style={{ marginLeft: 8 }}
              >
                {value ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
              </IconButton>
            </div>
          );
        } else if(value){
          return <span>{value}</span>;
        }
      },
    },
    {
      field: 'sent_at',
      headerName: 'Date Received',
      width: 250,
      valueGetter: params => {
        return params.row.referral.receivedFormattedDate();
      },
      sortComparator: DateTimeUtils.dateSortComparator,
      renderCell: (params) => {
        const value = params.row.referral.receivedFormattedDate();
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>{value}</span>
            <IconButton
              onClick={e => {
                e.stopPropagation();
                params.row.actions.onUpdateSentDate(params.row.referral);
              }}
              size="small"
              style={{ marginLeft: 8 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </div>
        );
      }
    },
    referralIsOrderColumnItem,
    {
      field: 'updated_by',
      headerName: 'Last Updated By',
      width: 250,
      valueGetter: params => {
        return params.row.referral.getLastUpdatedByFullName();
      },
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated At',
      width: 250,
      valueGetter: params => {
        return params.row.referral.formattedDate();
      },
      sortComparator: DateTimeUtils.dateSortComparator
    },
  ];

  return (
    <MyDataGrid
      onFilterChanged={onFilterChanged}
      onChangeDropdown={onChangeDropdown}
      onSelectedRowsChanged={onSelectedRowsChanged}
      loading={loading}
      data={data}
      columns={columns}
      onClick={onClick}
      showToolbar={true}
      selections={['By Name', 'By Birthdate']}
      addReferral={true}
      minTableHeight={'calc(100vh - 192px)'}
      getRowId={row => {
        return row.id;
      }}
      fromReceived={true}
    />
  );
}
